import { useEffect, useCallback, useRef } from 'react'
import { useVoice } from '../contexts/VoiceContext'

/**
 * Default keybinds configuration
 * These match what's displayed in KeybindsTab.jsx
 */
const DEFAULT_KEYBINDS = {
  toggleMute: { key: 'M', ctrl: true, shift: true },
  toggleDeafen: { key: 'D', ctrl: true, shift: true },
  pushToTalk: { key: ' ', ctrl: true, shift: false }, // Ctrl+Space
  openSettings: { key: ',', ctrl: true, shift: false },
  search: { key: 'K', ctrl: true, shift: false },
}

/**
 * Check if a keyboard event matches a keybind config
 */
function matchesKeybind(event, keybind) {
  const keyMatches = event.key.toUpperCase() === keybind.key.toUpperCase() ||
    (keybind.key === ' ' && event.code === 'Space')
  const ctrlMatches = keybind.ctrl ? (event.ctrlKey || event.metaKey) : true
  const shiftMatches = keybind.shift ? event.shiftKey : !event.shiftKey

  return keyMatches && ctrlMatches && shiftMatches
}

/**
 * useKeybinds Hook
 * Registers global keyboard shortcuts for voice controls and app navigation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onOpenSettings - Callback when settings shortcut triggered
 * @param {Function} options.onSearch - Callback when search shortcut triggered
 */
export function useKeybinds({ onOpenSettings, onSearch } = {}) {
  const voice = useVoice()
  const pushToTalkRef = useRef(false)

  // Handle keydown events
  const handleKeyDown = useCallback((event) => {
    // Don't trigger shortcuts when typing in inputs
    const target = event.target
    const isTyping = target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable ||
      target.closest('[contenteditable="true"]')

    // Allow push-to-talk even when typing (it's a hold-to-talk feature)
    // But block other shortcuts when typing
    const isPushToTalk = matchesKeybind(event, DEFAULT_KEYBINDS.pushToTalk)

    if (isTyping && !isPushToTalk) {
      return
    }

    // Toggle Mute: Ctrl+Shift+M
    if (matchesKeybind(event, DEFAULT_KEYBINDS.toggleMute)) {
      event.preventDefault()
      voice.toggleMute()
      return
    }

    // Toggle Deafen: Ctrl+Shift+D
    if (matchesKeybind(event, DEFAULT_KEYBINDS.toggleDeafen)) {
      event.preventDefault()
      voice.toggleDeafen()
      return
    }

    // Push to Talk: Ctrl+Space (keydown starts talking)
    if (isPushToTalk && !pushToTalkRef.current) {
      event.preventDefault()
      pushToTalkRef.current = true
      voice.startPushToTalk()
      return
    }

    // Open Settings: Ctrl+,
    if (matchesKeybind(event, DEFAULT_KEYBINDS.openSettings)) {
      event.preventDefault()
      onOpenSettings?.()
      return
    }

    // Search: Ctrl+K
    if (matchesKeybind(event, DEFAULT_KEYBINDS.search)) {
      event.preventDefault()
      onSearch?.()
      return
    }
  }, [voice, onOpenSettings, onSearch])

  // Handle keyup events (for push-to-talk release)
  const handleKeyUp = useCallback((event) => {
    // Push to Talk: Ctrl+Space (keyup stops talking)
    if (matchesKeybind(event, DEFAULT_KEYBINDS.pushToTalk) && pushToTalkRef.current) {
      event.preventDefault()
      pushToTalkRef.current = false
      voice.stopPushToTalk()
    }
  }, [voice])

  // Register event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyDown, handleKeyUp])

  return {
    keybinds: DEFAULT_KEYBINDS
  }
}

export default useKeybinds
